import { useEffect, useRef } from 'react';

import { interpolateFromKeyframes } from '../utils/interpolateFromKeyframes';

/**
 * Figma exit state keyframe positions (as progress 0–1).
 * Applied identically whether the card exits toward the top or bottom.
 *
 * Figma states at: 15%, 10%, 8%, 5%, 1% from edge
 * Mapped to progress within the animation range:
 *   start → 0.0
 *   10%   → 0.357
 *    8%   → 0.5
 *    5%   → 0.714
 *    1%   → 1.0
 */
const KEYFRAME_POSITIONS = [0.0, 0.357, 0.5, 0.714, 1.0];

/** Rotation keyframes: 0° at start → -120° counterclockwise at full exit */
const ROTATION_KEYFRAMES = [-0.0, -25.35, -59.88, -109.1, -120.0];

/** Scale keyframes: 100% → 0% as card exits (simultaneous with rotation) */
const SCALE_KEYFRAMES = [1.0, 0.55, 0.4, 0.2, 0.0];

/**
 * Exit animation triggers when 30% of the card is outside the viewport.
 * This means only 70% remains visible — matching the entrance threshold.
 */
const EXIT_VISIBILITY_RATIO = 0.7;

/**
 * Animation completes when only 1% of the trigger zone remains.
 */
const ANIMATION_END_FRACTION = 0.05;

/**
 * @deprecated CSS custom property name — kept for backward compatibility.
 * The hook now sets style.transform directly instead of using CSS variables.
 */
export const EXIT_CSS_VAR_TRANSFORM = '--book-exit-transform';

interface UseScrollExitAnimationOptions {
  /** When true, no observers are created */
  disabled?: boolean;
}

interface UseScrollExitAnimationResult {
  /** Ref to attach to the element that should animate on exit */
  exitRef: React.RefObject<HTMLDivElement>;
}

/**
 * Scroll-tethered exit animation hook.
 *
 * Animates (rotation + scale) as the card exits the viewport from either
 * the top or bottom edge. Exit triggers when 70%+ of the card is outside
 * the viewport (only 30% remains visible).
 *
 * Updates style.transform SYNCHRONOUSLY in the scroll handler (no rAF).
 * This is critical: rAF defers the update to the next frame, but the browser's
 * compositor has already scrolled the element to its new position. With
 * will-change: transform, the compositor shows a stale compositing layer
 * snapshot for one frame before rAF updates it — causing visible flicker.
 *
 * Based on Figma exit state design (node 10516:96838).
 */
export function useScrollExitAnimation(options: UseScrollExitAnimationOptions = {}): UseScrollExitAnimationResult {
  const { disabled = false } = options;

  const exitRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) return undefined;

    const el = exitRef.current;
    if (!el) return undefined;

    // Cache viewport height — only changes on resize
    let viewportHeight = window.innerHeight;

    const handleResize = (): void => {
      viewportHeight = window.innerHeight;
    };

    const computeAndApply = (): void => {
      const currentEl = exitRef.current;
      if (!currentEl) return;

      const rect = currentEl.getBoundingClientRect();
      const cardHeight = rect.height;

      // How much of the card is visible in the viewport
      const visibleTop = Math.max(0, rect.top);
      const visibleBottom = Math.min(viewportHeight, rect.bottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibleFraction = cardHeight > 0 ? visibleHeight / cardHeight : 1;

      let clampedProgress = 0;

      if (visibleFraction >= EXIT_VISIBILITY_RATIO) {
        // Card is sufficiently visible (>=30%) — no exit animation
        clampedProgress = 0;
      } else if (visibleFraction <= 0) {
        // Card is fully off-screen
        clampedProgress = 1;
      } else {
        // Card is between 0% and 30% visible — animate exit
        // Progress goes from 0 (at 30% visible) to 1 (at ~5% visible)
        const exitRange = EXIT_VISIBILITY_RATIO - ANIMATION_END_FRACTION;
        const progressInRange = (EXIT_VISIBILITY_RATIO - visibleFraction) / exitRange;
        clampedProgress = Math.max(0, Math.min(1, progressInRange));
      }

      const rotation = interpolateFromKeyframes(clampedProgress, ROTATION_KEYFRAMES, KEYFRAME_POSITIONS);
      const scale = interpolateFromKeyframes(clampedProgress, SCALE_KEYFRAMES, KEYFRAME_POSITIONS);

      // Synchronous DOM mutation — must happen in the scroll handler itself,
      // NOT in a rAF callback. rAF creates a 1-frame lag where the compositor
      // shows the element at its new scroll position with the OLD transform.
      currentEl.style.transform = `rotate(${rotation}deg) scale(${scale})`;
    };

    // Set transform-origin once (doesn't change per frame)
    el.style.transformOrigin = 'center center';

    // Initial check
    computeAndApply();

    // SYNCHRONOUS scroll handler — no requestAnimationFrame debouncing.
    window.addEventListener('scroll', computeAndApply, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', computeAndApply);
      window.removeEventListener('resize', handleResize);
    };
  }, [disabled]);

  return { exitRef };
}
